// src/utils/formatters.ts

import { globalComposer } from '@/i18n';

/**
 * Formats a time-to-live value in seconds to a human-readable, localized string.
 *
 * Reuses the same `web.UNITS.ttl.duration` / `web.UNITS.ttl.time.*` keys (and
 * plural forms) as `usePrivacyOptions.ts`'s `formatDuration`, and reads the
 * global i18n composer directly (see `usePageTitle.ts`) so this plain
 * function can be called from `computed()`s across many components without
 * requiring each call site to plumb through a `t` function. `globalComposer.t`
 * reads the reactive `locale` ref internally, so callers wrapping this in a
 * `computed()` still re-render on locale change.
 *
 * @param seconds - The TTL value in seconds
 * @returns A formatted, localized string representing the time period
 */
export const formatTTL = (seconds: number): string => {
  const { t } = globalComposer;

  if (seconds >= 86400) {
    const count = Math.floor(seconds / 86400);
    return t('web.UNITS.ttl.duration', { count, unit: t('web.UNITS.ttl.time.day', count) });
  }
  if (seconds >= 3600) {
    const count = Math.floor(seconds / 3600);
    return t('web.UNITS.ttl.duration', { count, unit: t('web.UNITS.ttl.time.hour', count) });
  }
  const count = Math.floor(seconds / 60);
  return t('web.UNITS.ttl.duration', { count, unit: t('web.UNITS.ttl.time.minute', count) });
};
